import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Container,
  IconButton,
  CircularProgress,
  Switch,
  Collapse,
  useTheme,
} from '@mui/material';
import ManIcon from '@mui/icons-material/Man';
import WomanIcon from '@mui/icons-material/Woman';
import SettingsIcon from '@mui/icons-material/Settings';
import { PageHeader, SectionTitle, SelectableCard, QuantityStepper, useToast } from '../Components/ui';
import { radius, colors } from '../theme';

// ─── Konfiguration ────────────────────────────────────────────────────────────

const ACTIVITIES = [
  { value: 1.2,     icon: '💺', label: 'Kaum aktiv',     desc: 'Bürojob, wenig Bewegung' },
  { value: 1.375,   icon: '🚶', label: 'Leicht aktiv',   desc: '1–3× Sport pro Woche' },
  { value: 1.55,    icon: '🏃', label: 'Mäßig aktiv',    desc: '3–5× Sport pro Woche' },
  { value: 1.725,   icon: '🏋️', label: 'Sehr aktiv',     desc: '6–7× Sport pro Woche' },
  { value: 1.9,     icon: '⚡', label: 'Extrem aktiv',   desc: 'Tägl. intensiver Sport' },
];

const GOALS = [
  { value: 'stark_abnehmen',   icon: '🔥', label: 'Stark abnehmen',   desc: '−30 % Kalorien',          color: colors.goal.aggressive },
  { value: 'moderat_abnehmen', icon: '📉', label: 'Moderat abnehmen', desc: '−15 % Kalorien',          color: colors.goal.moderate },
  { value: 'gewicht_halten',   icon: '⚖️', label: 'Gewicht halten',   desc: 'Ausgeglichene Ernährung', color: colors.goal.maintain },
];

// ─── Hilfskomponenten ─────────────────────────────────────────────────────────

const EmojiIcon = ({ children }) => (
  <Typography sx={{ fontSize: '1.5rem', lineHeight: 1 }}>{children}</Typography>
);

const BodyStepper = ({ label, unit, ...stepperProps }) => (
  <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
      <QuantityStepper size={28} fieldWidth={48} fontSize="1rem" gap={0} {...stepperProps} />
    </Box>
    <Typography variant="caption" color="text.secondary">{unit}</Typography>
  </Box>
);

// ─── Kalorienberechnung ───────────────────────────────────────────────────────

const calcCalories = (age, height, weight, gender, activity, goal) => {
  if (!age || !height || !weight || !gender) return null;
  const bmr = gender === 'male'
    ? 88.36 + 13.4 * weight + 4.8 * height - 5.7 * age
    : 447.6 + 9.2  * weight + 3.1 * height - 4.3 * age;
  const tdee = bmr * activity;
  let result = tdee;
  if (goal === 'stark_abnehmen')   result = Math.max(tdee * 0.70, 1200);
  if (goal === 'moderat_abnehmen') result = Math.max(tdee * 0.85, 1200);
  return Math.round(result);
};

// ─── Hauptkomponente ──────────────────────────────────────────────────────────

const Profile = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const token = localStorage.getItem('token');
  const { showToast } = useToast();

  const [age,            setAge]      = useState('');
  const [height,         setHeight]   = useState('');
  const [weight,         setWeight]   = useState('');
  const [gender,         setGender]   = useState('');
  const [activityLevel,  setActivity] = useState(1.2);
  const [goal,           setGoal]     = useState('gewicht_halten');
  const [manualMode,     setManualMode]     = useState(false);
  const [manualCalories, setManualCalories] = useState('');
  const [settingsExist,  setSettingsExist] = useState(false);
  const [isLoading,      setIsLoading]     = useState(true);
  const [isSaving,       setIsSaving]      = useState(false);

  const liveCalories = useMemo(
    () => calcCalories(Number(age), Number(height), Number(weight), gender, activityLevel, goal),
    [age, height, weight, gender, activityLevel, goal]
  );

  // Das ist der Wert, der am Ende gespeichert und überall (Dashboard, Streaks)
  // als Tagesziel verwendet wird.
  const targetCalories = manualMode
    ? (Number(manualCalories) > 0 ? Number(manualCalories) : null)
    : liveCalories;

  const handleToggleManual = (checked) => {
    setManualMode(checked);
    // Beim ersten Umschalten den berechneten Wert als Startpunkt übernehmen
    if (checked && !manualCalories && liveCalories) setManualCalories(liveCalories);
  };

  useEffect(() => {
    const load = async () => {
      if (!token) return;
      try {
        const res = await fetch('/api/users/get-settings', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const d = await res.json();
          setSettingsExist(true);
          setAge(d.age || '');
          setHeight(d.height || '');
          setWeight(d.weight || '');
          setGender(d.gender || '');
          setActivity(d.activity_level || 1.2);
          setGoal(d.goal || 'gewicht_halten');
          setManualMode(Boolean(d.manual_calories));
          if (d.manual_calories && d.calories) setManualCalories(d.calories);
        } else if (res.status === 404) {
          setSettingsExist(false);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [token]);

  const handleSave = async () => {
    if (!targetCalories) {
      showToast(
        manualMode ? 'Bitte ein Kalorienziel eintragen.' : 'Bitte alle Felder ausfüllen.',
        { severity: 'warning' }
      );
      return;
    }
    setIsSaving(true);
    try {
      const endpoint = settingsExist ? '/api/users/update-settings' : '/api/users/insert-settings';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          age: age === '' ? null : Number(age),
          height: height === '' ? null : Number(height),
          weight: weight === '' ? null : Number(weight),
          gender: gender || null,
          activity_level: activityLevel, goal,
          calories: targetCalories,
          manual_calories: manualMode,
        }),
      });
      if (res.ok) {
        setSettingsExist(true);
        showToast('Profil gespeichert ✓');
      } else {
        showToast('Fehler beim Speichern.', { severity: 'error' });
      }
    } catch {
      showToast('Verbindungsfehler.', { severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100dvh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 3, pb: 14 }}>

      {/* Header */}
      <PageHeader
        title="Mein Profil"
        trailingAction={
          <IconButton onClick={() => navigate('/settings')} size="small" sx={{ border: 1, borderColor: 'divider' }}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        }
      />

      {/* Körperdaten */}
      <SectionTitle
        action={manualMode && (
          <Typography variant="caption" color="text.secondary">
            optional
          </Typography>
        )}
      >
        Körperdaten
      </SectionTitle>
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          px: 1,
          py: 1.5,
          borderRadius: radius.xl,
          border: 1,
          borderColor: 'divider',
          mb: 3,
          overflow: 'hidden',
        }}
      >
        <BodyStepper label="Alter"   value={age}    unit="Jahre" onChange={setAge}    min={10} max={100} />
        <Box sx={{ width: '1px', bgcolor: 'divider' }} />
        <BodyStepper label="Größe"   value={height} unit="cm"    onChange={setHeight} min={100} max={250} />
        <Box sx={{ width: '1px', bgcolor: 'divider' }} />
        <BodyStepper label="Gewicht" value={weight} unit="kg"    onChange={setWeight} min={30}  max={300} />
      </Box>

      {/* Geschlecht */}
      <SectionTitle>Geschlecht</SectionTitle>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
        {[
          { value: 'male',   label: 'Männlich', Icon: ManIcon,   selectColor: 'primary.main' },
          { value: 'female', label: 'Weiblich', Icon: WomanIcon, selectColor: colors.gender.female },
        ].map(({ value, label, Icon, selectColor }) => (
          <Box key={value} sx={{ flex: 1 }}>
            <SelectableCard
              layout="vertical"
              selected={gender === value}
              onClick={() => setGender(value)}
              icon={<Icon sx={{ fontSize: 32 }} />}
              label={label}
              color={selectColor}
            />
          </Box>
        ))}
      </Box>

      {/* Kalorienziel: berechnen lassen oder selbst festlegen */}
      <SectionTitle>Kalorienziel</SectionTitle>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1.5,
          borderRadius: radius.xl,
          border: 1,
          borderColor: 'divider',
          mb: manualMode ? 3 : 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" fontWeight={600}>
            Selbst festlegen
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {manualMode
              ? 'Du gibst dein Tagesziel direkt vor'
              : 'Wird aus Körperdaten, Aktivität und Ziel berechnet'}
          </Typography>
        </Box>
        <Switch
          checked={manualMode}
          onChange={(e) => handleToggleManual(e.target.checked)}
          inputProps={{ 'aria-label': 'Kalorienziel selbst festlegen' }}
        />
      </Box>

      {/* Aktivität + Ziel — nur relevant, wenn berechnet wird */}
      <Collapse in={!manualMode} timeout="auto" unmountOnExit>
        <SectionTitle>Aktivitätslevel</SectionTitle>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          {ACTIVITIES.map((a) => (
            <SelectableCard
              key={a.value}
              selected={activityLevel === a.value}
              onClick={() => setActivity(a.value)}
              icon={<EmojiIcon>{a.icon}</EmojiIcon>}
              label={a.label}
              desc={a.desc}
            />
          ))}
        </Box>

        <SectionTitle>Dein Ziel</SectionTitle>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
          {GOALS.map((g) => (
            <SelectableCard
              key={g.value}
              selected={goal === g.value}
              onClick={() => setGoal(g.value)}
              icon={<EmojiIcon>{g.icon}</EmojiIcon>}
              label={g.label}
              desc={g.desc}
              color={g.color}
            />
          ))}
        </Box>
      </Collapse>

      {/* Tagesziel — berechnet oder selbst eingetragen */}
      {(manualMode || liveCalories) && (
        <Box
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: radius.xl,
            background: `linear-gradient(135deg, ${theme.palette.brand.gradientStart} 0%, ${theme.palette.brand.gradientEnd} 100%)`,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mb: manualMode ? 1.5 : 0.5 }}>
            {manualMode ? 'Dein tägliches Kalorienziel' : 'Dein täglicher Kalorienbedarf'}
          </Typography>

          {manualMode ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 1.5,
                '& .MuiIconButton-root': { color: 'common.white', borderColor: 'rgba(255,255,255,0.45)' },
                '& .MuiInputBase-input': { color: '#fff' },
                '& .MuiInput-root:before': { borderBottomColor: 'rgba(255,255,255,0.45)' },
                '& .MuiInput-root:hover:not(.Mui-disabled):before': { borderBottomColor: 'rgba(255,255,255,0.7)' },
                '& .MuiInput-root:after': { borderBottomColor: '#fff' },
              }}
            >
              <QuantityStepper
                value={manualCalories}
                onChange={setManualCalories}
                min={500}
                max={9000}
                step={50}
                fieldWidth={110}
                fontSize="1.75rem"
              />
            </Box>
          ) : (
            <Typography variant="h4" fontWeight="bold" sx={{ color: 'common.white' }}>
              {liveCalories} kcal
            </Typography>
          )}

          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {manualMode ? 'kcal · selbst festgelegt' : 'Wird automatisch berechnet'}
          </Typography>
        </Box>
      )}

      {/* Speichern */}
      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={handleSave}
        disabled={isSaving || !targetCalories}
        sx={{ py: 1.75, fontSize: '1rem' }}
      >
        {isSaving ? <CircularProgress size={22} color="inherit" /> : 'Speichern'}
      </Button>

    </Container>
  );
};

export default Profile;
